import { useEffect, useState } from "react";
// Frameworks
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Menu,
  Skeleton,
  Box,
  Avatar,
  Divider,
  MenuItem,
  IconButton,
  ListItemIcon,
  Typography,
} from "@mui/material";
import useAppDispatch from "hooks/useAppDispatch";
import { show } from "store/reducers/notifications.reducer";
// Assets
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import HelpIcon from "@mui/icons-material/Help";

// 🚀 usa tu axios con interceptores (añade x-user-hash solo)
import customAxios from "lib/axios";
import axios from "axios"; // solo para axios.isCancel

type LanguageOption = {
  value: string;
  label: string;
  icon: JSX.Element;
};

type UserZoneProps = {
  helpHref?: string;
  currentLanguage?: string;
  onLanguageChange?: (language: string) => void;
  languages?: LanguageOption[];
};

const UserZone = function ({
  helpHref,
  currentLanguage,
  onLanguageChange,
  languages = [],
}: UserZoneProps) {
  const [username, setUsername] = useState("");
  const [avatarimg, setavatarimg] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [t] = useTranslation(["dashboard"]);
  const dispatch = useAppDispatch();
  const open = Boolean(anchorEl);

  useEffect(() => {
    const userId = localStorage.getItem("user_id");

    // ❌ Si no hay sesión, no dispares llamadas
    if (!userId) {
      setUsername("");
      setavatarimg("");
      setLoading(false);
      return;
    }

    setLoading(true);

    // ✅ Cancelación (Axios v1)
    const controller = new AbortController();

    (async () => {
      try {
        const { data } = await customAxios.get(`/users`, {
          signal: controller.signal,
        });
        setUsername(data?.alias ?? "");
        setavatarimg(data?.avatar_img ?? "");
      } catch (error: any) {
        // Ignora si fue cancelada
        if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") return;

        dispatch(
          show({
            type: "error",
            msg: "Error del servidor, contacte con el soporte.",
          })
        );
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [dispatch]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onLogout = () => {
    try {
      localStorage.removeItem("user_id");
      localStorage.removeItem("user_email");
      localStorage.removeItem("type_user");
      localStorage.removeItem("user_valid_count");
    } catch {
      console.log("error al remover user");
    }
    window.location.reload();
  };

  const firstLetter = username?.trim()?.[0]?.toUpperCase() || "?";

  return (
    <Box
      display="flex"
      alignItems="center"
      bgcolor="white"
      borderRadius="5px"
      mx={{ xs: 0, sm: 1 }}
    >
      <Avatar
        variant="square"
        style={{ width: "40px", height: "40px", borderRadius: 5 }}
        src={avatarimg || undefined}
        alt={username || "user"}
      >
        {firstLetter}
      </Avatar>

      <Box mx={1} display="flex" alignItems="center">
        <Typography
          variant="body1"
          noWrap
          color="textSecondary"
          display="none"
          sx={{
            padding: { md: "0 6px" },
            display: { md: "block" },
          }}
        >
          {isLoading ? <Skeleton width={50} /> : username || ""}
        </Typography>

        <IconButton color="primary" onClick={handleOpen} size="small">
          <ArrowDropDownIcon />
        </IconButton>
      </Box>

      <Menu open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItem component={Link} to="/profile" onClick={handleClose}>
          {t("profile")}
        </MenuItem>
        {!!helpHref && (
          <MenuItem
            component="a"
            href={helpHref}
            target="_blank"
            rel="noreferrer"
            onClick={handleClose}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <HelpIcon fontSize="small" />
            </ListItemIcon>
            {t("helpCenter")}
          </MenuItem>
        )}
        {languages.length > 0 && onLanguageChange && (
          <>
            <Divider />
            {languages.map((language) => (
              <MenuItem
                key={language.value}
                selected={currentLanguage === language.value}
                onClick={() => {
                  onLanguageChange(language.value);
                  handleClose();
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {language.icon}
                </ListItemIcon>
                <Typography
                  variant="body2"
                  fontWeight={
                    currentLanguage === language.value ? 700 : undefined
                  }
                >
                  {language.label}
                </Typography>
              </MenuItem>
            ))}
          </>
        )}
        <Divider />
        <MenuItem onClick={onLogout}>{t("logout")}</MenuItem>
      </Menu>
    </Box>
  );
};

export default UserZone;
